// CreateNews.js (updated with edit functionality)
import React, { useState, useEffect } from 'react';
import { Form, Row, Col, Button, Container, Alert, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import BaseUrl from '../../ApiServices/BaseUrl';
import AdminNavbar from '../../Pages/Navbar/AdminNavbar';
import "./CreateNews.css"

function CreateNews() {
  const navigate = useNavigate();
  const { id } = useParams(); // Get news ID from URL if editing
  const isEditMode = Boolean(id);
  
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    publish_date: '',
    read_time: '',
    excerpt: '',
    content: '',
    image_url: '',
    status: 'draft',
    slug: '',
    meta_description: '',
    tags: ''
  });
  
  const [selectedImage, setSelectedImage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [existingImageUrl, setExistingImageUrl] = useState('');

  // Fetch news data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      fetchNewsData();
    }
  }, [id]);

  const fetchNewsData = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${BaseUrl}/api/news/${id}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch news data');
      }
      
      const newsData = await response.json();
      
      // Format date for datetime-local input
      const formattedDate = newsData.publish_date 
        ? new Date(newsData.publish_date).toISOString().slice(0, 16)
        : '';
      
      setFormData({
        title: newsData.title || '',
        category: newsData.category || '',
        publish_date: formattedDate,
        read_time: newsData.read_time || '',
        excerpt: newsData.excerpt || '',
        content: newsData.content || '',
        image_url: newsData.image_url || '',
        status: newsData.status || 'draft',
        slug: newsData.slug || '',
        meta_description: newsData.meta_description || '',
        tags: newsData.tags || ''
      });
      
      setExistingImageUrl(newsData.image_url || '');
      
    } catch (error) {
      console.error('Error fetching news data:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Error loading news article: ' + error.message,
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#d33',
      }).then(() => {
        navigate('/managenews');
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Auto-generate slug when title changes
    if (name === 'title' && !formData.slug) {
      const generatedSlug = generateSlug(value);
      setFormData(prev => ({
        ...prev,
        slug: generatedSlug
      }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const previewUrl = URL.createObjectURL(file);
      setFormData({
        ...formData,
        image_url: previewUrl
      });
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const finalFormData = {
        ...formData,
        slug: formData.slug || generateSlug(formData.title)
      };

      // Handle image upload if a new image is selected
      if (selectedImage) {
        const imageFormData = new FormData();
        imageFormData.append('image', selectedImage);
        
        const uploadResponse = await fetch(`${BaseUrl}/api/upload`, {
          method: 'POST',
          body: imageFormData,
        });
        
        if (uploadResponse.ok) {
          const uploadResult = await uploadResponse.json();
          finalFormData.image_url = uploadResult.imageUrl;
        } else {
          throw new Error('Failed to upload image');
        }
      } else if (!finalFormData.image_url && existingImageUrl) {
        // Keep existing image if no new image is selected
        finalFormData.image_url = existingImageUrl;
      }
      
      const url = isEditMode ? `${BaseUrl}/api/news/${id}` : `${BaseUrl}/api/news`;
      const method = isEditMode ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(finalFormData),
      });
      
      if (response.ok) {
        const successMessage = isEditMode 
          ? 'News article updated successfully!' 
          : 'News article created successfully!';
        
        Swal.fire({
          title: 'Success!',
          text: successMessage,
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#0056a3',
        }).then(() => {
          navigate('/managenews');
        });
      } else {
        const errorText = await response.text();
        throw new Error(errorText || `Failed to ${isEditMode ? 'update' : 'create'} news article`);
      }
    } catch (error) {
      console.error('Error:', error);
      Swal.fire({
        title: 'Error!',
        text: `Error ${isEditMode ? 'updating' : 'creating'} news article: ${error.message}`,
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#d33',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setFormData({
      ...formData,
      image_url: ''
    });
    setExistingImageUrl('');
    // Clear file input
    const fileInput = document.getElementById('formImage');
    if (fileInput) fileInput.value = '';
  };

  if (isLoading) {
    return (
      <>
        <AdminNavbar />
        <Container className="manage-news" style={{ marginTop: '100px' }}>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        </Container>
      </>
    );
  }

  return (
    <>
      <AdminNavbar />
      <Container className="manage-news">
            <div className="d-flex flex-row justify-content-between align-items-center mb-4" style={{marginTop:"100px"}}>
          <h2 className="mb-2">
            {isEditMode ? 'Edit News Article' : 'Create News Article'}
          </h2>
          <Button 
            variant="secondary" 
            onClick={() => navigate('/managenews')}
            className="manage-news-btn"
          >
            Back to Manage News
          </Button>
        </div>

        <div className="form-box">
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="formTitle">
                  <Form.Label>Title *</Form.Label>
                  <Form.Control 
                    type="text" 
                    name="title"
                    placeholder="Enter article title" 
                    value={formData.title}
                    onChange={handleInputChange}
                    required 
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="formCategory">
                  <Form.Label>Category *</Form.Label>
                  <Form.Control 
                    type="text" 
                    name="category"
                    placeholder="Enter category" 
                    value={formData.category}
                    onChange={handleInputChange}
                    required 
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="formPublishDate">
                  <Form.Label>Publish Date & Time</Form.Label>
                  <Form.Control 
                    type="datetime-local" 
                    name="publish_date"
                    value={formData.publish_date}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="formReadTime">
                  <Form.Label>Read Time</Form.Label>
                  <Form.Control 
                    type="text" 
                    name="read_time"
                    placeholder="e.g., 5 min read" 
                    value={formData.read_time}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3" controlId="formExcerpt">
              <Form.Label>Excerpt *</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={3} 
                name="excerpt"
                placeholder="Short description of the article" 
                value={formData.excerpt}
                onChange={handleInputChange}
                required 
              />
              <Form.Text className="text-muted">
                A brief summary that will appear in news listings.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formContent">
              <Form.Label>Content *</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={8} 
                name="content"
                placeholder="Article content" 
                value={formData.content}
                onChange={handleInputChange}
                required 
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="formImage">
                  <Form.Label>Featured Image</Form.Label>
                  <Form.Control 
                    type="file" 
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                  {(formData.image_url || existingImageUrl) && (
                    <div className="mt-3">
                      <p className="small text-muted mb-1">Image Preview:</p>
                      <div className="position-relative d-inline-block">
                        <img 
                          src={formData.image_url || existingImageUrl} 
                          alt="Preview" 
                          style={{maxWidth: '100%', maxHeight: '200px', borderRadius: '0px'}}
                        />
                        <Button
                          variant="danger"
                          size="sm"
                          className="position-absolute top-0 end-0 m-1"
                          onClick={handleRemoveImage}
                          style={{ opacity: 0.9 }}
                        >
                          ×
                        </Button>
                      </div>
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="formStatus">
                  <Form.Label>Status *</Form.Label>
                  <Form.Select 
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="formSlug">
                  <Form.Label>Slug</Form.Label>
                  <Form.Control 
                    type="text" 
                    name="slug"
                    placeholder="URL-friendly version of title" 
                    value={formData.slug}
                    onChange={handleInputChange}
                  />
                  <Form.Text className="text-muted">
                    Auto-generated from title if empty. Use hyphens instead of spaces.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="formTags">
                  <Form.Label>Tags</Form.Label>
                  <Form.Control 
                    type="text" 
                    name="tags"
                    placeholder="Comma-separated tags" 
                    value={formData.tags}
                    onChange={handleInputChange}
                  />
                  <Form.Text className="text-muted">
                    e.g., business,technology,innovation
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3" controlId="formMetaDescription">
              <Form.Label>Meta Description</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={3} 
                name="meta_description"
                placeholder="SEO meta description" 
                value={formData.meta_description}
                onChange={handleInputChange}
              />
              <Form.Text className="text-muted">
                This will appear in search engine results. Recommended: 150-160 characters.
              </Form.Text>
            </Form.Group>

            <div className="row">
              <div className="col-md-12">
              <div className="createnews">
<div className="createNews-buttons d-flex gap-2">
  <Button 
    variant="primary" 
    type="submit" 
    className="submit-btn flex-fill"
    disabled={isSubmitting}
  >
    {isSubmitting ? (
      <>
        <Spinner animation="border" size="sm" className="me-2" />
        {isEditMode ? 'Updating...' : 'Publishing...'}
      </>
    ) : (
      isEditMode ? 'Update Article' : 'Publish Article'
    )}
  </Button>

  <Button 
    variant="outline-secondary"
    onClick={() => navigate('/managenews')}
    className="cancel-btn"
    disabled={isSubmitting}
  >
    Cancel
  </Button>
</div>

</div>

              </div>
            </div>
          </Form>
        </div>
      </Container>
    </>
  );
}

export default CreateNews;

